/**
 * Incluidos vs extras en ficha de producto (frontend).
 * Campos opcionales: included_items[], included_summary, upsell_items[].
 * Fallback: description + name cuando no hay datos explícitos.
 */

import { normalizeMenuBlob } from '@/lib/menuSemanticTags';
import { getProductChoiceGroups } from '@/lib/productChoiceGroups';

function mergeUniqueIngredientNames(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      const name = String(raw ?? '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
  }
  return out;
}

const FRIES_RE = /\b(papa|papas|frita|fritas|porci[oó]n\s+de\s+papas|acompañ\w*)\b/i;
const DRINK_RE =
  /\b(bebida|gaseosa|jugo|coca|sprite|pepsi|refresco|agua\s+mineral|schop|litro|500\s*cc)\b/i;

/** @param {unknown} product */
export function getProductIncludedItems(product) {
  const raw = product?.included_items ?? product?.includedItems;
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x ?? '').trim()).filter(Boolean);
}

/** @param {unknown} product */
export function getProductIncludedSummary(product) {
  const items = getProductIncludedItems(product);
  if (items.length) return items.join(', ');
  const s = product?.included_summary ?? product?.includedSummary;
  if (s != null && String(s).trim()) return String(s).trim();
  return '';
}

/** Ingredientes de ficha (removibles) para consulta en cocina. */
export function getProductIngredientNames(product) {
  const raw =
    product?.removable_ingredients ??
    product?.removableIngredients ??
    product?.ingredients ??
    product?.product_ingredients ??
    product?.productIngredients;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => {
      if (x == null) return '';
      if (typeof x === 'string') return x.trim();
      return String(x?.name || x?.label || '').trim();
    })
    .filter(Boolean);
}

/** Removibles + opciones de grupos de elección (p. ej. arma tu pizza) para KDS. */
export function getProductKitchenIngredientNames(product) {
  const fromRemovable = getProductIngredientNames(product);
  const fromChoices = getProductChoiceGroups(product).flatMap((g) =>
    g.options.map((o) => o.name).filter(Boolean),
  );
  return mergeUniqueIngredientNames(fromRemovable, fromChoices);
}

/** Campos a persistir en el ítem del pedido (cocina / ticket). */
export function productIncludedFieldsForOrderLine(product) {
  const included_items = getProductIncludedItems(product);
  const summaryRaw = product?.included_summary ?? product?.includedSummary;
  const included_summary =
    summaryRaw != null && String(summaryRaw).trim()
      ? String(summaryRaw).trim()
      : included_items.length
        ? included_items.join(', ')
        : undefined;
  const product_ingredients = getProductKitchenIngredientNames(product);
  const descRaw = product?.description;
  const product_description =
    descRaw != null && String(descRaw).trim() ? String(descRaw).trim() : undefined;
  const stationRaw = product?.station;
  const station =
    stationRaw != null && String(stationRaw).trim()
      ? String(stationRaw).trim().toLowerCase()
      : undefined;
  return {
    included_items,
    ...(included_summary ? { included_summary } : {}),
    ...(product_ingredients.length ? { product_ingredients } : {}),
    ...(product_description ? { product_description } : {}),
    ...(station ? { station } : {}),
  };
}

/** @param {unknown} product */
export function getProductUpsellItems(product) {
  const raw = product?.upsell_items ?? product?.upsellItems;
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x ?? '').trim()).filter(Boolean);
}

/** True si el negocio cargó incluidos explícitos (no solo inferencia por descripción). */
export function productHasExplicitAssistantBundle(product) {
  if (getProductIncludedItems(product).length) return true;
  const s = product?.included_summary ?? product?.includedSummary;
  return s != null && String(s).trim().length > 0;
}

/** Blob normalizado para búsqueda / detección (incluye incluidos + desc fallback). */
export function productCompositionBlob(product) {
  const parts = [
    product?.name,
    getProductIncludedSummary(product),
    ...getProductIncludedItems(product),
    getProductUpsellItems(product).join(' '),
    product?.description,
    product?.category,
  ].filter(Boolean);
  return normalizeMenuBlob(parts.join(' '));
}

/** @param {unknown} product */
export function productIncludedHasFries(product) {
  const b = productCompositionBlob(product);
  return FRIES_RE.test(b);
}

/** @param {unknown} product */
export function productIncludedHasDrink(product) {
  const b = productCompositionBlob(product);
  return DRINK_RE.test(b);
}

/** Hamburguesa / burger por nombre o categoría (heurística liviana). */
export function productIsBurgerish(product) {
  const b = productCompositionBlob(product);
  return /\b(hamburguesa|burger|hamburg|smash)\b/.test(b);
}

/** Papas / side sin ser hamburguesa principal (para filtrar upsell redundante). */
export function productIsFriesSideCandidate(product) {
  const b = productCompositionBlob(product);
  if (!FRIES_RE.test(b)) return false;
  if (/\b(hamburguesa|burger|hamburg|combo\s+con)\b/.test(b)) return false;
  return true;
}

/** Bebida como ítem suelto (upsell útil). */
export function productLooksLikeDrinkItem(product) {
  const b = productCompositionBlob(product);
  if (!DRINK_RE.test(b)) return false;
  if (/\b(hamburguesa|burger|pizza|completo)\b/.test(b)) return false;
  return true;
}

/** Sugiere extra proteína (nombre o add-ons). */
export function productSuggestsExtraProteinAddon(product) {
  const b = productCompositionBlob(product);
  if (/\b(extra\s+carne|doble\s+carne|carne\s+extra|extra\s+pollo|doble\s+queso)\b/.test(b)) {
    return true;
  }
  const a = product?.add_ons;
  if (!Array.isArray(a)) return false;
  return a.some((x) => {
    const n = normalizeMenuBlob(typeof x === 'string' ? x : x?.name || '');
    return /\b(extra|doble|carne|pollo|bacon|cheddar)\b/.test(n);
  });
}

/**
 * Mayoría sobre un conjunto de productos: 'all' | 'none' | 'majority' | 'minority' | 'split'
 * (majority = más de la mitad cumple el predicado; minority = menos de la mitad).
 */
export function majorityIncludedFlag(products, predicate) {
  const list = products || [];
  const t = list.length;
  if (!t) return 'none';
  const n = list.filter(predicate).length;
  if (n === t) return 'all';
  if (n === 0) return 'none';
  if (n * 2 > t) return 'majority';
  if (n * 2 < t) return 'minority';
  return 'split';
}

/** Primer agregado cuyo nombre coincide con expresión (p. ej. bebida). */
export function firstAddOnByNameMatch(product, re) {
  const a = product?.add_ons;
  if (!Array.isArray(a)) return null;
  for (const x of a) {
    const name = String(x?.name ?? '').trim();
    if (!name) continue;
    if (re.test(normalizeMenuBlob(name))) {
      return { name, price: Number(x?.price) || 0 };
    }
  }
  return null;
}

export function firstDrinkLikeAddOn(product) {
  return firstAddOnByNameMatch(product, DRINK_RE);
}

/** Nombre de extra (add_on) suena a bebida — para ordenar upsell rápido. */
export function addOnNameLooksDrinkLike(name) {
  return DRINK_RE.test(normalizeMenuBlob(String(name || '')));
}
